'use client';

import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { FlightSearch } from '../../../flights/search/ui/FlightSearch';
import { IFlightsEnrichmentData } from '../../../flights/shared/flight/domain/models/IEnrichedFlight';
import { useSetFlightsEnrichmentData } from '../../../flights/shared/flight/ui/useSetFlightsEnrichmentData';
import { useFlightStores } from '../../../flights/shared/context/flightStoresContext';
import { ContentWrap, Css, ITheme, Responsive } from '../../../../ui';
import { Color } from '../../../../styles/Color';
import { StyledToggleLink } from '../../../flights/overview/ui/FlightsScopeToggle';
import { AirportCodes } from '../../../../types/airports';
import { getShopDineRelaxListUrl } from '../../../shop-dine-relax/use-cases/getShopDineRelaxListUrl';
import { PoiListScope } from '../../../shop-dine-relax/search/domain/models/PoiListScope';
import { ParkingBookingWidgetForm } from '../../../parking-availability/ui/ParkingBookingWidgetForm';
import { FONT_FAMILY_MUSEO_SANS_CONDENSED } from '../../../../styles/Font';
import { AirportCodesWithParkingBooking } from '../../../parking-availability/domain/models/AirportParkingBookingRoute';

interface IProps {
	airport: AirportCodes;
	flightsEnrichmentData: IFlightsEnrichmentData;
}

type SearchScope = 'flights' | 'parking';

export const LandingSearchWidget = observer(
	({ airport, flightsEnrichmentData }: IProps) => {
		const [scope, setScope] = useState<SearchScope>('flights');

		const { flightListStore } = useFlightStores();

		const onSetScope = (
			e: React.MouseEvent<HTMLButtonElement>,
			scopeToSet: SearchScope,
		) => {
			e.preventDefault();
			setScope(scopeToSet);
			return false;
		};

		useSetFlightsEnrichmentData(flightsEnrichmentData);
		useEffect(() => {
			flightListStore.initialLoad();
		}, []);

		return (
			<StyledContentWrap>
				<Wrapper>
					<LinkRow>
						<li className="no-max-width">
							<StyledToggleButtonLink
								onClick={(e) => onSetScope(e, 'flights')}
								aria-label="Navigate to the flights page"
								aria-current={scope === 'flights' ? 'page' : false}
							>
								<h4>Flights</h4>
							</StyledToggleButtonLink>
						</li>
						<li className="no-max-width">
							<StyledToggleButtonLink
								onClick={(e) => onSetScope(e, 'parking')}
								aria-label="Navigate to the parking availability page"
								aria-current={scope === 'parking' ? 'page' : false}
							>
								<h4>Parking</h4>
							</StyledToggleButtonLink>
						</li>
						<li className="no-max-width">
							<StyledToggleLink
								href={getShopDineRelaxListUrl({
									airportCode: airport,
									scope: PoiListScope.All,
								})}
								role="button"
								aria-label="Navigate to the shop dine and relax page"
							>
								<h4>Shop/Dine/Relax</h4>
							</StyledToggleLink>
						</li>
					</LinkRow>
					{scope === 'flights' && (
						<FlightSearch isFullWidth airport={airport} submitOnClear={false} />
					)}
					{scope === 'parking' && (
						<ParkingBookingWidgetForm
							airportCode={airport as AirportCodesWithParkingBooking}
							isLanding
						/>
					)}
				</Wrapper>
			</StyledContentWrap>
		);
	},
);

const LinkRow = styled.ul`
	display: flex;
	flex-wrap: wrap;
	margin: 0;
	padding: 0;
	list-style: none;
	gap: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		gap: ${Css.pxToRem(16)};
	}
`;

const StyledContentWrap = styled(ContentWrap)<{}, ITheme>`
	position: relative;
	padding: 0;
	display: grid;
	grid-column-gap: ${(props) => props.theme.grid?.gutter?.vertical?.default};
	grid-template-columns: 2fr 0fr;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		grid-template-columns: 1fr;
	}
`;

const Wrapper = styled.div<{}, ITheme>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(24)};
	background: ${(props) => props.theme.background?.default};
	border: 1px solid ${Color.BorderCard};
	border-radius: 8px;
	padding: ${Css.pxToRem(32)};
	padding-top: ${Css.pxToRem(24)};
	margin-top: -${Css.pxToRem(224)};
	margin-bottom: ${Css.pxToRem(90)};
	max-width: ${Css.pxToRem(912)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: -${Css.pxToRem(270)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-top: -${Css.pxToRem(140)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(16)};
		gap: ${Css.pxToRem(16)};
		margin-top: ${Css.pxToRem(-135)};
		margin-bottom: ${Css.pxToRem(50)};
	}
`;

const StyledToggleButtonLink = styled.button<{}, ITheme>`
	text-decoration: none;
	border: none;
	background: none;
	padding: 0;
	font-family: ${FONT_FAMILY_MUSEO_SANS_CONDENSED};

	h4 {
		margin: 0;
		color: ${Color.Neutral6};
		font-size: ${Css.pxToRem(32)};
		text-transform: capitalize;
	}

	&[aria-current='page'] {
		h4 {
			border-bottom: 2px solid ${(props) => props.theme.color.secondaryLight};
			color: ${(props) => props.theme.color.secondaryLight};
		}
	}

	&:hover,
	&:active {
		cursor: pointer;
		h4 {
			color: ${(props) => props.theme.color.secondaryLight};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		h4 {
			font-size: ${Css.pxToRem(20)};
		}
	}
`;
